/**
 * RenewalReminderBanner — surfaced on the Lab Dashboard when the subscription
 * is within 7 days of expiry (or already expired).
 *
 * Copy escalates by urgency:
 *   • > 7d       → hidden
 *   • 4-7d       → amber "expires soon"
 *   • 1-3d       → orange "expires in N days"
 *   • today / 0d → red "expires today"
 *   • past       → red "your subscription expired N days ago"
 *
 * "Renew now" button re-uses the exact Razorpay flow from `pages/lab/Account.jsx`
 * — same POST /payments/subscription/order → openRazorpayCheckout → /payments/verify.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";

function daysBetween(dateStr) {
  if (!dateStr) return null;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((t - Date.now()) / (1000 * 60 * 60 * 24));
}

function urgency(days) {
  if (days === null || days > 7) return null;
  if (days > 3) return { level: "amber", tone: "border-amber-300 bg-amber-50", accent: "text-amber-700", btn: "bg-amber-500 hover:bg-amber-600 text-white" };
  if (days > 0) return { level: "orange", tone: "border-orange-300 bg-orange-50", accent: "text-orange-700", btn: "bg-orange-500 hover:bg-orange-600 text-white" };
  return { level: "red", tone: "border-red-300 bg-red-50", accent: "text-red-700", btn: "bg-red-600 hover:bg-red-700 text-white" };
}

function copyFor(days) {
  if (days === null) return null;
  if (days > 3) return { title: `Your subscription renews in ${days} day${days === 1 ? "" : "s"}`, sub: "Renew now to avoid any interruption to patient report processing." };
  if (days > 0) return { title: `Your subscription expires in ${days} day${days === 1 ? "" : "s"}`, sub: "Renew now — a few taps and you're set for the next billing cycle." };
  if (days === 0) return { title: "Your subscription expires today", sub: "Renew now to keep reports flowing without interruption." };
  const overdue = Math.abs(days);
  return { title: `Your subscription expired ${overdue} day${overdue === 1 ? "" : "s"} ago`, sub: "Renew immediately to restore report processing and marketplace visibility." };
}

export default function RenewalReminderBanner() {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payments/subscription/current");
      setCurrent(r.data?.current || null);
    } catch { /* silent — dashboard shouldn't blow up if billing endpoint fails */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const renew = async () => {
    if (!current?.plan_id || !current?.plan) {
      toast.error("Plan details missing — open Subscription page to choose a plan.");
      return;
    }
    setBusy(true);
    try {
      const { data: order } = await api.post("/payments/subscription/order", {
        plan_id: current.plan_id,
        billing: current.billing || "monthly",
      });
      const result = await openRazorpayCheckout({
        key_id: order.key_id,
        order_id: order.order_id,
        amount_paise: order.amount_paise,
        description: `EFGH · Renew ${current.plan.name} (${current.billing || "monthly"})`,
        prefill: { name: user?.full_name || "", email: user?.email || "", contact: user?.phone || "9876543210" },
        theme_color: "#F97316",
      });
      if (!result) { setBusy(false); return; }
      const v = await api.post("/payments/verify", {
        razorpay_order_id: result.razorpay_order_id,
        razorpay_payment_id: result.razorpay_payment_id,
        razorpay_signature: result.razorpay_signature,
      });
      toast.success(`Renewed — new expiry ${(v.data.expires_at || "").slice(0, 10)}`);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Renewal failed"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  const days = daysBetween(current?.expires_at);
  const u = urgency(days);
  if (!u) return null;
  const text = copyFor(days);
  if (!text) return null;

  return (
    <div
      className={`mb-5 p-4 rounded-2xl border ${u.tone} flex items-start gap-3`}
      data-testid={`renewal-banner-${u.level}`}
    >
      {days < 1 ? <AlertTriangle className={`w-5 h-5 ${u.accent} shrink-0 mt-0.5`} /> : <CalendarClock className={`w-5 h-5 ${u.accent} shrink-0 mt-0.5`} />}
      <div className="flex-1 min-w-0">
        <div className={`font-heading font-semibold text-ink flex items-center gap-2`}>
          {text.title}
          {current?.plan?.name && <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${u.accent}`}>{current.plan.name} · {current.billing || "monthly"}</span>}
        </div>
        <div className="text-sm text-ink/70 mt-0.5">{text.sub}</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button
            data-testid="renewal-banner-renew"
            onClick={renew}
            disabled={busy}
            className={`h-9 px-4 ${u.btn}`}
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Razorpay…</> : <>Renew now →</>}
          </Button>
          <Link to="/lab/billing" data-testid="renewal-banner-manage">
            <Button variant="ghost" className="h-9 px-4 text-ink/70 hover:text-ink">Manage subscription</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
